/**
 * HandlungsempfehlungenOverlayContent Component
 * Displays detailed Handlungsempfehlungen (Action Recommendations) data in the overlay
 * with items grouped into funnel categories (Awareness, Consideration, Conversion, Loyalty)
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lightbulb, Clock, Users, ChevronDown, ChevronUp, CheckCircle2, ArrowRight, Target, Megaphone, Search, ShoppingCart, Heart } from 'lucide-react';
import { getPriorityColor, getEffectColor } from '@/types/handlungsempfehlungen';
import { FUNNEL_CATEGORIES } from '@/types/massnahmen';
import type { FunnelCategory } from '@/types/massnahmen';
import type { HandlungsempfehlungenFullData, HandlungsempfehlungTableEntry } from '@/types/handlungsempfehlungen';
import { SectionDescriptionText } from '@/components/results/SectionDescriptionText';
import type { GetDescriptionFn } from '@/types/sectionDescription';

export interface HandlungsempfehlungenOverlayContentProps {
  data: HandlungsempfehlungenFullData | string;
  getDescription?: GetDescriptionFn;
  featureName?: string;
}

const CATEGORY_ICONS: Record<FunnelCategory, typeof Megaphone> = {
  Awareness: Megaphone,
  Consideration: Search,
  Conversion: ShoppingCart,
  Loyalty: Heart,
};

const CATEGORY_COLORS: Record<FunnelCategory, { from: string; to: string }> = {
  Awareness: { from: '#FC03B0', to: '#FF6803' },
  Consideration: { from: '#6366f1', to: '#8b5cf6' },
  Conversion: { from: '#22c55e', to: '#16a34a' },
  Loyalty: { from: '#f59e0b', to: '#d97706' },
};

/**
 * Single recommendation item with expandable accordion
 */
function RecommendationItem({
  recommendation,
  index,
  isExpanded,
  onToggle,
  colors,
  t,
}: {
  recommendation: HandlungsempfehlungTableEntry;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
  colors: { from: string; to: string };
  t: (key: string) => string;
}) {
  // Sort Umsetzungsplan by Schritt
  const sortedSteps = [...(recommendation.Umsetzungsplan || [])]
    .sort((a, b) => a.Schritt - b.Schritt);

  return (
    <div className="bg-white rounded-[var(--radius-md)] border border-[var(--glass-border)] overflow-hidden transition-all duration-200">
      {/* Header - always visible */}
      <div
        className="p-4 cursor-pointer hover:bg-[var(--glass-bg)]/50 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-start gap-3">
          {/* Rank badge */}
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
            style={{
              background: `linear-gradient(135deg, ${colors.from}, ${colors.to})`,
            }}
          >
            {index + 1}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h4 className="font-semibold text-[var(--color-charcoal)] text-sm">
                {recommendation.Name}
              </h4>
              {/* Expand/Collapse icon */}
              {isExpanded ? (
                <ChevronUp className="h-5 w-5 text-[var(--color-charcoal)]/50 flex-shrink-0" />
              ) : (
                <ChevronDown className="h-5 w-5 text-[var(--color-charcoal)]/50 flex-shrink-0" />
              )}
            </div>

            <p className="text-xs text-[var(--color-charcoal)]/70 mt-1 line-clamp-2">
              {recommendation.Beschreibung}
            </p>

            {/* Table-like info display */}
            <div className="mt-3 bg-[var(--glass-bg)] rounded-lg p-3">
              <table className="w-full text-xs">
                <tbody>
                  <tr>
                    <td className="py-1.5 pr-4 text-[var(--color-charcoal)]/50 whitespace-nowrap align-middle w-[140px]">
                      <div className="flex items-center gap-1.5">
                        <Users className="h-4 w-4" />
                        {t('results.cards.handlungsempfehlungen.overlay.targetGroup')}
                      </div>
                    </td>
                    <td className="py-1.5 text-[var(--color-charcoal)] font-medium align-middle">
                      {recommendation.Zielgruppe}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-1.5 pr-4 text-[var(--color-charcoal)]/50 whitespace-nowrap align-middle w-[140px]">
                      <div className="flex items-center gap-1.5">
                        <Clock className="h-4 w-4" />
                        {t('results.cards.handlungsempfehlungen.overlay.effectAfter')}
                      </div>
                    </td>
                    <td className="py-1.5 font-medium align-middle" style={{ color: getEffectColor(recommendation.Effekt_nach_Monaten) }}>
                      {recommendation.Effekt_nach_Monaten} {t('results.cards.handlungsempfehlungen.overlay.monthsLabel')}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-1.5 pr-4 text-[var(--color-charcoal)]/50 whitespace-nowrap align-middle w-[140px]">
                      <div className="flex items-center gap-1.5">
                        <Target className="h-4 w-4" />
                        {t('results.cards.handlungsempfehlungen.overlay.priorityLabel')}
                      </div>
                    </td>
                    <td className="py-1.5 align-middle">
                      <span
                        className="inline-flex items-center gap-1.5 font-medium"
                        style={{ color: getPriorityColor(recommendation.Priorität) }}
                      >
                        <span
                          className="w-2.5 h-2.5 rounded-full"
                          style={{ backgroundColor: getPriorityColor(recommendation.Priorität) }}
                        />
                        {t('results.cards.handlungsempfehlungen.overlay.priority')} {recommendation.Priorität}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* CTA Button - shown when collapsed */}
            {!isExpanded && (
              <div className="mt-3">
                <a
                  href={import.meta.env.VITE_CTA_REDIRECT_URL || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="h-7 text-xs btn-primary inline-flex items-center px-3 rounded-md"
                  onClick={(e) => e.stopPropagation()}
                >
                  {recommendation.CTA_Text}
                  <ArrowRight className="h-3.5 w-3.5 ml-1" />
                </a>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Expandable Umsetzungsplan section */}
      {isExpanded && (
        <div className="border-t border-[var(--glass-border)] bg-[var(--glass-bg)]/30">
          <div className="p-4">
            <h5 className="text-xs font-semibold text-[var(--color-charcoal)] mb-3 flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-[var(--color-primary-start)]" />
              {t('results.cards.handlungsempfehlungen.overlay.implementationPlan')}
            </h5>

            {/* Steps list */}
            <div className="space-y-3">
              {sortedSteps.map((step) => (
                <div
                  key={step.Schritt}
                  className="flex gap-3 bg-white p-3 rounded-lg border border-[var(--glass-border)]"
                >
                  {/* Step number */}
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[var(--color-primary-start)]/20 to-[var(--color-primary-end)]/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-[var(--color-primary-start)]">
                      {step.Schritt}
                    </span>
                  </div>
                  {/* Step content */}
                  <div className="flex-1 min-w-0">
                    <h6 className="text-sm font-medium text-[var(--color-charcoal)]">
                      {step.Name}
                    </h6>
                    <p className="text-xs text-[var(--color-charcoal)]/70 mt-0.5">
                      {step.Beschreibung}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA Button inside accordion */}
            <div className="mt-4 flex justify-end">
              <a
                href={import.meta.env.VITE_CTA_REDIRECT_URL || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex items-center px-4 py-2 rounded-md"
              >
                {recommendation.CTA_Text}
                <ArrowRight className="h-4 w-4 ml-2" />
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Category accordion section for Handlungsempfehlungen
 */
function CategorySection({
  category,
  items,
  isCategoryExpanded,
  onToggleCategory,
  expandedItems,
  onToggleItem,
  t,
  getDescription,
  featureName,
}: {
  category: FunnelCategory;
  items: HandlungsempfehlungTableEntry[];
  isCategoryExpanded: boolean;
  onToggleCategory: () => void;
  expandedItems: Set<string>;
  onToggleItem: (key: string) => void;
  t: (key: string) => string;
  getDescription?: GetDescriptionFn;
  featureName?: string;
}) {
  const Icon = CATEGORY_ICONS[category];
  const colors = CATEGORY_COLORS[category];
  const categoryKey = category.toLowerCase() as Lowercase<FunnelCategory>;

  return (
    <div className="bg-[var(--glass-bg)] rounded-[var(--radius-md)] border border-[var(--glass-border)] overflow-hidden">
      {/* Category header */}
      <button
        className="w-full p-4 flex items-center gap-3 hover:bg-white/50 transition-colors cursor-pointer"
        onClick={onToggleCategory}
      >
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: `linear-gradient(135deg, ${colors.from}, ${colors.to})` }}
        >
          <Icon className="h-5 w-5 text-white" />
        </div>
        <div className="flex-1 text-left">
          <h4 className="text-sm font-semibold text-[var(--color-charcoal)]">
            {t(`results.cards.categories.${categoryKey}`)}
          </h4>
        </div>
        <span className="text-xs font-medium text-[var(--color-charcoal)]/50 bg-white/60 px-2 py-0.5 rounded-full">
          {items.length}
        </span>
        {isCategoryExpanded ? (
          <ChevronUp className="h-5 w-5 text-[var(--color-charcoal)]/50 flex-shrink-0" />
        ) : (
          <ChevronDown className="h-5 w-5 text-[var(--color-charcoal)]/50 flex-shrink-0" />
        )}
      </button>

      {/* Collapsible content: list of recommendation items */}
      {isCategoryExpanded && (
        <div className="border-t border-[var(--glass-border)] p-4 space-y-3">
          <SectionDescriptionText getDescription={getDescription} featureName={featureName} sectionKey={category.toLowerCase()} />
          {items.map((recommendation, index) => {
            const itemKey = `${category}-${index}`;
            return (
              <RecommendationItem
                key={itemKey}
                recommendation={recommendation}
                index={index}
                isExpanded={expandedItems.has(itemKey)}
                onToggle={() => onToggleItem(itemKey)}
                colors={colors}
                t={t}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Overlay content for Handlungsempfehlungen with full details
 */
export function HandlungsempfehlungenOverlayContent({
  data,
  getDescription,
  featureName,
}: HandlungsempfehlungenOverlayContentProps) {
  const { t } = useTranslation();
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(() => {
    const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;
    return isMobile ? new Set<string>() : new Set(['Awareness']);
  });
  const [expandedItems, setExpandedItems] = useState<Set<string>>(() => new Set());

  // Handle legacy string data
  if (typeof data === 'string') {
    return (
      <div className="space-y-6">
        <div className="hidden md:flex items-center gap-2 md:gap-3 pb-1 md:pb-2">
          <div className="p-2 md:p-2.5 rounded-lg bg-gradient-to-br from-[var(--color-primary-start)]/10 to-[var(--color-primary-end)]/10">
            <Lightbulb className="h-5 w-5 md:h-6 md:w-6 text-[var(--color-primary-start)]" />
          </div>
          <h3 className="text-base md:text-lg font-semibold leading-none tracking-tight text-[var(--color-charcoal)]">
            {t('results.cards.handlungsempfehlungen.title')}
          </h3>
        </div>
        <div className="p-4 bg-[var(--glass-bg)] rounded-[var(--radius-md)] border border-[var(--glass-border)]">
          <p className="text-sm text-[var(--color-charcoal)]/80 leading-relaxed whitespace-pre-wrap">
            {data}
          </p>
        </div>
      </div>
    );
  }

  const { main } = data;

  // Get categorized data — handle both legacy flat array and new categorized format
  const categorizedTable = Array.isArray(main.Handlungsempfehlungen_tabelle)
    ? { Awareness: main.Handlungsempfehlungen_tabelle as HandlungsempfehlungTableEntry[], Consideration: [], Conversion: [], Loyalty: [] }
    : main.Handlungsempfehlungen_tabelle || { Awareness: [], Consideration: [], Conversion: [], Loyalty: [] };

  const handleToggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const handleToggleItem = (key: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  return (
    <div className="space-y-6">
      {/* Header with icon and title */}
      <div className="hidden md:flex items-center gap-2 md:gap-3 pb-1 md:pb-2">
        <div className="p-2 md:p-2.5 rounded-lg bg-gradient-to-br from-[var(--color-primary-start)]/10 to-[var(--color-primary-end)]/10">
          <Lightbulb className="h-5 w-5 md:h-6 md:w-6 text-[var(--color-primary-start)]" />
        </div>
        <h3 className="text-base md:text-lg font-semibold leading-none tracking-tight text-[var(--color-charcoal)]">
          {t('results.cards.handlungsempfehlungen.title')}
        </h3>
      </div>

      {/* Subtitle */}
      <p className="text-sm text-[var(--color-charcoal)]/70">
        {t('results.cards.handlungsempfehlungen.overlay.subtitle')}
      </p>

      {/* Category Accordion Sections */}
      <div className="space-y-3">
        {FUNNEL_CATEGORIES.map(category => {
          const items = categorizedTable[category] || [];
          return (
            <CategorySection
              key={category}
              category={category}
              items={items}
              isCategoryExpanded={expandedCategories.has(category)}
              onToggleCategory={() => handleToggleCategory(category)}
              expandedItems={expandedItems}
              onToggleItem={handleToggleItem}
              t={t}
              getDescription={getDescription}
              featureName={featureName}
            />
          );
        })}
      </div>
    </div>
  );
}

export default HandlungsempfehlungenOverlayContent;
